import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { cta, CTA_STYLES, CTA_COLORS } from "@/lib/cta";
import { BRAND } from "@/lib/brand";
import heroBackground from "@/assets/hero-background.jpg";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { SiteFooter } from "@/components/layout/SiteFooter";
import { SiteHeader } from "@/components/layout/SiteHeader";
import { ScrollToTop } from "@/components/ui/scroll-to-top";

/**
 * Buyers Landing Page (FR/EN) – Standalone
 * - Geist font applied throughout
 * - EN is the default language
 */

const THEME = BRAND;



// ===== Copy (EN default, FR included) =====
const copy = {
  en: {
    hero: {
      eyebrow: "BUYERS",
      title: "Stop searching for a business, become the ideal buyer",
      subtitle:
        "AcquizGroup gives you privileged access to the 'Invisible Market,' advanced financial analysis tools, and a network of unconventional lenders, all centralized on a single platform.",
      ctaLocal: "I'm a local buyer",
      ctaIntl: "I'm an international buyer",
    },
    mission: {
      title: "The Power of the Network",
      description:
        "More than a platform, we are facilitators of partnerships and operational structuring dedicated to the success of your business transfer.",
    },
    phases: [
      {
        title: "Exploration & Qualification",
        steps: [
          { title: "Private Profile", desc: "Create your account and define your search criteria." },
          { title: "Privileged Access", desc: "Choose the package that suits your ambitions." },
          { title: "The Deal Monitor", desc: "Explore qualified and exclusive opportunities." },
        ],
      },
      {
        title: "Analysis & Engagement",
        steps: [
          { title: "Financial Engineering", desc: "Instantly visualize financing options for each company." },
          { title: "One-Click Confidentiality", desc: "Digitally sign your NDAs to access sensitive data." },
          { title: "Direct Connection", desc: "Start communicating directly with sellers or brokers." },
        ],
      },
      {
        title: "Offer & Finalization",
        steps: [
          { title: "Formal Commitment", desc: "Generate and submit your Letter of Intent (LOI)." },
          { title: "Capital Optimization", desc: "Select the ideal loan structure from the offers received." },
          { title: "Closing", desc: "Finalize your acquisition with complete confidence." },
        ],
      },
    ],
    credibility: {
      docs: {
        title: "Included documents",
        desc: "Save time with our vault of ready-to-use documents (NDA, LOI, standard contracts).",
      },
      booster: {
        title: "Credibility Booster",
        desc: "Access our directory of consultants for free to build a strong team of buyers and reassure your financial partners.",
      },
      team: {
        title: "Your Success Team: Consultants & Experts",
        intro:
          "To reassure banks and secure your investment, AcquizGroup provides you with two specialized networks, accessible directly from your dashboard.",
        consultant: {
          title: "1. The Consultant Program: Strengthen Your Buyer Profile",
          intro:
            "Don't go it alone. Integrate cutting-edge expertise to enhance your credibility with sellers and lenders.",
          items: [
            {
              h: "Strategic Enhancement",
              p: "Recruit advisors or business partners with the industry expertise, licenses, or key skills that are missing from your profile.",
            },
            {
              h: "Business Partnership",
              p: "Identify strategic allies to integrate directly into your deal structure to solidify your acquisition proposal.",
            },
          ],
        },
        experts: {
          title: "2. The Experts Program: Secure the Transaction",
          intro:
            "Gain direct access to the essential specialists you need to cross the finish line in full compliance.",
          items: [
            {
              h: "Due Diligence & Closing",
              p: "Collaborate with our in-house experts (Business and Immigration Lawyers, Notaries, CPAs, and Tax Advisors).",
            },
            {
              h: "Risk Management",
              p: "Rigorous technical support to ensure that every legal and tax aspect of your acquisition is optimized.",
            },
          ],
        },
      },
    },
    faq: {
      title: "Frequently Asked Questions",
      items: [
        {
          q: "How do I access business opportunities?",
          a: "After creating your profile and selecting a plan, you'll have access to our Deal Monitor where you can browse all qualified and exclusive opportunities.",
        },
        {
          q: "What financing options are available?",
          a: "We connect you with both traditional and non-conventional lenders. You can visualize financing options directly on each opportunity listing.",
        },
        {
          q: "How does the NDA process work?",
          a: "Once you find an opportunity of interest, you can digitally sign an NDA with one click to access confidential business information.",
        },
        {
          q: "Can I get help structuring my offer?",
          a: "Yes, through our Consultant Program you can recruit advisors and business partners to strengthen your acquisition proposal.",
        },
        {
          q: "What support is available for closing?",
          a: "Our Experts Program provides access to Business Lawyers, Immigration Lawyers, Notaries, CPAs, and Tax Advisors to ensure a compliant closing.",
        },
        {
          q: "Is there support for international buyers?",
          a: "Absolutely. We provide specialized support for entrepreneurial immigration projects in North America.",
        },
      ],
    },
    cta: {
      title: "Ready to acquire your business?",
      subtitle: "Join our network and find the right opportunity.",
      primary: "Sign up as a buyer",
      secondary: "Contact the team",
    },
  },
  fr: {
    hero: {
      eyebrow: "ACHETEURS",
      title: "Trouvez l'opportunité qui vous correspond",
      subtitle: "Dossiers structurés, Listing et accès aux prêteurs traditionnels et non conventionnels.",
      ctaLocal: "Je suis un acheteur",
      ctaIntl: "Je suis un acheteur international",
    },
    mission: {
      title: "La puissance du réseau",
      description:
        "Nous sommes des facilitateurs de partenariats, de transmission d'entreprise et de structuration opérationnelle.",
    },
    phases: [
      {
        title: "Phase A : Exploration et qualification",
        steps: [
          { title: "Profil privé", desc: "Créez votre compte et définissez vos critères de recherche." },
          { title: "Accès privilégié", desc: "Choisissez le forfait qui correspond à vos ambitions." },
          { title: "Le Deal Monitor", desc: "Explorez des opportunités qualifiées et exclusives." },
        ],
      },
      {
        title: "Phase B : Analyse et engagement",
        steps: [
          {
            title: "Ingénierie financière",
            desc: "Visualisez instantanément les options de financement pour chaque entreprise.",
          },
          {
            title: "Confidentialité en un clic",
            desc: "Signez vos NDA numériquement pour accéder aux données sensibles.",
          },
          { title: "Connexion directe", desc: "Commencez à communiquer directement avec les vendeurs ou courtiers." },
        ],
      },
      {
        title: "Phase C : Offre et finalisation",
        steps: [
          { title: "Engagement formel", desc: "Générez et soumettez votre Lettre d'intention (LOI)." },
          {
            title: "Optimisation du capital",
            desc: "Sélectionnez la structure de prêt idéale parmi les offres reçues.",
          },
          { title: "Closing", desc: "Finalisez votre acquisition en toute confiance." },
        ],
      },
    ],
    credibility: {
      docs: {
        title: "Documents inclus",
        desc: "Gagnez du temps avec notre coffre-fort de documents prêts à l'emploi (NDA, LOI, contrats standards).",
      },
      booster: {
        title: "Booster de crédibilité",
        desc: "Accédez gratuitement à notre répertoire de consultants pour constituer une équipe d'acheteurs solide et rassurer vos partenaires financiers.",
      },
      team: {
        title: "Votre équipe de réussite : Consultants et Experts",
        intro:
          "Pour rassurer les banques et sécuriser votre investissement, AcquizGroup met à votre disposition deux réseaux spécialisés, accessibles directement depuis votre tableau de bord.",
        consultant: {
          title: "1. Le Programme Consultant : Renforcez votre profil d'acheteur",
          intro:
            "Ne restez pas seul. Intégrez une expertise de pointe pour renforcer votre crédibilité auprès des vendeurs et des prêteurs.",
          items: [
            {
              h: "Renforcement stratégique",
              p: "Recrutez des conseillers ou partenaires d'affaires possédant l'expertise sectorielle, les licences ou les compétences clés manquantes à votre profil.",
            },
            {
              h: "Partenariat d'affaires",
              p: "Identifiez des alliés stratégiques à intégrer directement dans la structure de votre transaction pour solidifier votre proposition d'acquisition.",
            },
          ],
        },
        experts: {
          title: "2. Le Programme Experts : Sécurisez la transaction",
          intro:
            "Accédez directement aux spécialistes essentiels dont vous avez besoin pour franchir la ligne d'arrivée en toute conformité.",
          items: [
            {
              h: "Due Diligence et Closing",
              p: "Collaborez avec nos experts internes (avocats d'affaires et en immigration, notaires, CPA et conseillers fiscaux).",
            },
            {
              h: "Gestion des risques",
              p: "Un support technique rigoureux pour garantir que chaque aspect juridique et fiscal de votre acquisition est optimisé.",
            },
          ],
        },
      },
    },
    faq: {
      title: "Questions fréquentes",
      items: [
        {
          q: "Comment accéder aux opportunités d'affaires ?",
          a: "Après avoir créé votre profil et sélectionné un forfait, vous aurez accès à notre Deal Monitor où vous pouvez parcourir toutes les opportunités qualifiées et exclusives.",
        },
        {
          q: "Quelles options de financement sont disponibles ?",
          a: "Nous vous connectons avec des prêteurs traditionnels et non conventionnels. Vous pouvez visualiser les options de financement directement sur chaque listing d'opportunité.",
        },
        {
          q: "Comment fonctionne le processus de NDA ?",
          a: "Une fois que vous trouvez une opportunité intéressante, vous pouvez signer un NDA numériquement en un clic pour accéder aux informations commerciales confidentielles.",
        },
        {
          q: "Puis-je obtenir de l'aide pour structurer mon offre ?",
          a: "Oui, grâce à notre Programme Consultant, vous pouvez recruter des conseillers et partenaires d'affaires pour renforcer votre proposition d'acquisition.",
        },
        {
          q: "Quel support est disponible pour le closing ?",
          a: "Notre Programme Experts donne accès à des avocats d'affaires, avocats en immigration, notaires, CPA et conseillers fiscaux pour assurer un closing conforme.",
        },
        {
          q: "Y a-t-il un support pour les acheteurs internationaux ?",
          a: "Absolument. Nous offrons un support spécialisé pour les projets d'immigration entrepreneuriale en Amérique du Nord.",
        },
      ],
    },
    cta: {
      title: "Prêt à acquérir votre entreprise ?",
      subtitle: "Rejoignez notre réseau et trouvez l'opportunité parfaite.",
      primary: "S'inscrire comme acheteur",
      secondary: "Contacter l'équipe",
    },
  },
} as const;

export default function BuyersLandingStandalone() {
  const [lang, setLang] = useState<"en" | "fr">("en");
  const t = useMemo(() => copy[lang], [lang]);
  const ctaTexts = cta("buyer", lang);

  return (
    <div className="min-h-screen bg-gray-50 font-sans">
      <SiteHeader lang={lang} onToggleLang={() => setLang(lang === "en" ? "fr" : "en")} />

      {/* Hero */}
      <section
        className="w-full relative overflow-hidden"
        style={{ background: `linear-gradient(180deg, ${THEME.navy}, ${THEME.navyDark})` }}
      >
        <div className="absolute inset-0 overflow-hidden flex items-center justify-center">
          <img src={heroBackground} alt="" className="min-w-full min-h-full object-cover blur-[2px] opacity-45" />
        </div>
        <div
          className="absolute inset-0"
          style={{ background: `linear-gradient(180deg, ${THEME.navy}CC, ${THEME.navyDark}CC)` }}
        ></div>
        <div className="relative mx-auto max-w-7xl px-4 py-10">
          <p className="text-sm uppercase font-sans" style={{ color: THEME.silver }}>
            {t.hero.eyebrow}
          </p>
          <h1 className="mt-2 text-4xl md:text-5xl font-extrabold text-white font-sans">{t.hero.title}</h1>
          <p className="mt-3 max-w-3xl text-base md:text-lg text-white/85 font-sans">{t.hero.subtitle}</p>
          <div className="mt-5 flex flex-wrap gap-3">
            <Link
              to="/register/buyers"
              className={CTA_STYLES.primary}
              style={{ background: `linear-gradient(90deg, ${CTA_COLORS.goldStart}, ${CTA_COLORS.goldEnd})` }}
            >
              {ctaTexts.primary}
            </Link>
            <Link
              to="/contact"
              aria-label={ctaTexts.aria}
              className={`${CTA_STYLES.secondary} border-white/30 text-white`}
            >
              {ctaTexts.secondary}
            </Link>
          </div>
        </div>
      </section>

      {/* Mission Section - The Power of the Network */}
      <section className="py-16 bg-gray-50">
        <div className="mx-auto max-w-4xl px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold font-sans" style={{ color: THEME.navy }}>
            {t.mission.title}
          </h2>
          <p className="mt-4 text-lg font-sans" style={{ color: THEME.graphite }}>
            {t.mission.description}
          </p>
        </div>
      </section>

      {/* How it works - 3 Phases */}
      <section className="py-16 bg-white">
        <div className="mx-auto max-w-7xl px-4">
          <h2 className="text-2xl md:text-3xl font-bold mb-12 text-center font-sans" style={{ color: THEME.navy }}>
            {lang === "fr" ? "Comment ça fonctionne" : "How it works"}
          </h2>

          <div className="space-y-12">
            {t.phases.map((phase, phaseIdx) => (
              <div key={phaseIdx}>
                <h3 className="text-xl font-semibold mb-6 font-sans" style={{ color: THEME.navy }}>
                  {phase.title}
                </h3>
                <div className="grid md:grid-cols-3 gap-6">
                  {phase.steps.map((step, stepIdx) => (
                    <div
                      key={stepIdx}
                      className="relative bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-all duration-300 border border-gray-100"
                    >
                      <div
                        className="absolute -top-4 -left-4 w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm shadow-lg font-sans"
                        style={{
                          background: `linear-gradient(135deg, ${THEME.goldStart}, ${THEME.goldEnd})`,
                          color: THEME.navy,
                        }}
                      >
                        {phaseIdx * 3 + stepIdx + 1}
                      </div>
                      <div className="pt-4">
                        <h4 className="font-semibold text-base mb-2 font-sans" style={{ color: THEME.navy }}>
                          {step.title}
                        </h4>
                        <p className="text-sm leading-relaxed font-sans" style={{ color: THEME.graphite }}>
                          {step.desc}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Learn More Button */}
          <div className="mt-10 text-center">
            <Link
              to="/general-landing"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-lg font-semibold text-sm transition-all duration-300 hover:shadow-lg font-sans"
              style={{
                background: `linear-gradient(90deg, ${THEME.goldStart}, ${THEME.goldEnd})`,
                color: THEME.navy,
              }}
            >
              {lang === "fr" ? "En savoir plus" : "Learn more"}
            </Link>
          </div>
        </div>
      </section>

      {/* Credibility Section */}
      <section className="py-16 bg-gray-50">
        <div className="mx-auto max-w-7xl px-4">
          {/* Docs and Booster */}
          <div className="grid md:grid-cols-2 gap-6 mb-12">
            <div
              className="relative rounded-xl p-8 shadow-sm hover:shadow-md transition-all duration-300 border border-gray-200"
              style={{
                background: "linear-gradient(135deg, #F5F1E8 0%, #FAF8F3 50%, #F0EBE3 100%)",
              }}
            >
              <div
                className="absolute -top-4 -left-4 w-12 h-12 rounded-full flex items-center justify-center shadow-lg"
                style={{
                  backgroundColor: THEME.navy,
                  color: "white",
                }}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                </svg>
              </div>
              <div className="pt-2">
                <h3 className="text-lg font-semibold mb-2 font-sans" style={{ color: THEME.navy }}>
                  {t.credibility.docs.title}
                </h3>
                <p className="text-sm leading-relaxed font-sans" style={{ color: THEME.graphite }}>
                  {t.credibility.docs.desc}
                </p>
              </div>
            </div>

            <div
              className="relative rounded-xl p-8 shadow-sm hover:shadow-md transition-all duration-300 border border-gray-200"
              style={{
                background: "linear-gradient(135deg, #F5F1E8 0%, #FAF8F3 50%, #F0EBE3 100%)",
              }}
            >
              <div
                className="absolute -top-4 -left-4 w-12 h-12 rounded-full flex items-center justify-center shadow-lg"
                style={{
                  backgroundColor: THEME.navy,
                  color: "white",
                }}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                  <circle cx="9" cy="7" r="4" />
                  <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
                  <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                </svg>
              </div>
              <div className="pt-2">
                <h3 className="text-lg font-semibold mb-2 font-sans" style={{ color: THEME.navy }}>
                  {t.credibility.booster.title}
                </h3>
                <p className="text-sm leading-relaxed font-sans" style={{ color: THEME.graphite }}>
                  {t.credibility.booster.desc}
                </p>
              </div>
            </div>
          </div>

          {/* Your Success Team */}
          <div
            className="rounded-2xl p-8 shadow-sm border border-gray-200"
            style={{
              background: "linear-gradient(135deg, #F5F1E8 0%, #FAF8F3 50%, #F0EBE3 100%)",
            }}
          >
            <h3 className="text-2xl font-bold mb-4 font-sans" style={{ color: THEME.navy }}>
              {t.credibility.team.title}
            </h3>
            <p className="text-base mb-8 font-sans" style={{ color: THEME.graphite }}>
              {t.credibility.team.intro}
            </p>

            <div className="grid md:grid-cols-2 gap-8">
              {/* Consultant Program */}
              <div className="bg-white rounded-xl p-6 shadow-sm">
                <h4 className="text-lg font-semibold mb-3 font-sans" style={{ color: THEME.navy }}>
                  {t.credibility.team.consultant.title}
                </h4>
                <p className="text-sm mb-4 font-sans" style={{ color: THEME.graphite }}>
                  {t.credibility.team.consultant.intro}
                </p>
                <ul className="space-y-4">
                  {t.credibility.team.consultant.items.map((item, idx) => (
                    <li key={idx}>
                      <h5 className="font-semibold text-sm font-sans" style={{ color: THEME.navy }}>
                        {item.h}
                      </h5>
                      <p className="text-sm mt-1 font-sans" style={{ color: THEME.graphite }}>
                        {item.p}
                      </p>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Experts Program */}
              <div className="bg-white rounded-xl p-6 shadow-sm">
                <h4 className="text-lg font-semibold mb-3 font-sans" style={{ color: THEME.navy }}>
                  {t.credibility.team.experts.title}
                </h4>
                <p className="text-sm mb-4 font-sans" style={{ color: THEME.graphite }}>
                  {t.credibility.team.experts.intro}
                </p>
                <ul className="space-y-4">
                  {t.credibility.team.experts.items.map((item, idx) => (
                    <li key={idx}>
                      <h5 className="font-semibold text-sm font-sans" style={{ color: THEME.navy }}>
                        {item.h}
                      </h5>
                      <p className="text-sm mt-1 font-sans" style={{ color: THEME.graphite }}>
                        {item.p}
                      </p>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Learn More Button */}
            <div className="mt-8 text-center md:col-span-2">
              <Link
                to="/general-landing"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-lg font-semibold text-sm transition-all duration-300 hover:shadow-lg font-sans"
                style={{
                  background: `linear-gradient(90deg, ${THEME.goldStart}, ${THEME.goldEnd})`,
                  color: THEME.navy,
                }}
              >
                {lang === "fr" ? "En savoir plus" : "Learn more"}
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-white">
        <div className="mx-auto max-w-4xl px-4">
          <h2 className="text-2xl md:text-3xl font-bold mb-10 text-center font-sans" style={{ color: THEME.navy }}>
            {t.faq.title}
          </h2>
          <Accordion type="single" collapsible className="space-y-4">
            {t.faq.items.map((item, idx) => (
              <AccordionItem
                key={idx}
                value={`item-${idx}`}
                className="bg-gray-50 rounded-xl border border-gray-200 px-6"
              >
                <AccordionTrigger
                  className="text-left font-semibold font-sans text-base py-4"
                  style={{ color: THEME.navy }}
                >
                  {item.q}
                </AccordionTrigger>
                <AccordionContent className="text-sm font-sans pb-4" style={{ color: THEME.graphite }}>
                  {item.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Final CTA */}
      <section className="mx-auto max-w-7xl px-4 pb-24">
        <div
          className="rounded-2xl p-8 text-center shadow-lg"
          style={{ background: `linear-gradient(180deg, ${THEME.navyDark}, ${THEME.navy})` }}
        >
          <h2 className="text-2xl font-semibold text-white font-sans">{t.cta.title}</h2>
          <p className="mt-2 text-sm font-sans" style={{ color: THEME.silver }}>
            {t.cta.subtitle}
          </p>
          <div className="mt-5 flex items-center justify-center gap-3 flex-wrap">
            <Link
              to="/buyer-plans"
              className={`${CTA_STYLES.secondary}`}
              style={{ borderColor: CTA_COLORS.goldStart, color: CTA_COLORS.goldStart }}
            >
              {lang === "fr" ? "Plans Acheteurs" : "Buyers Plan"}
            </Link>
            <Link
              to="/register/buyers"
              className={CTA_STYLES.primary}
              style={{ background: `linear-gradient(90deg, ${CTA_COLORS.goldStart}, ${CTA_COLORS.goldEnd})` }}
            >
              {ctaTexts.primary}
            </Link>
            <Link
              to="/contact"
              aria-label={ctaTexts.aria}
              className={`${CTA_STYLES.secondary} border-white/30 text-white`}
            >
              {ctaTexts.secondary}
            </Link>
          </div>
        </div>
      </section>

      <SiteFooter lang={lang} />
      <ScrollToTop />
    </div>
  );
}
